import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface Setor {
  nome: string;
  valor: number;
}

interface TabelaTaxas {
  nome?: string;
  descricao?: string;
  setores: Setor[];
}

const TabelaTaxas: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tabela, setTabela] = useState<TabelaTaxas | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exportandoPDF, setExportandoPDF] = useState(false);
  const [nomeEmpresa, setNomeEmpresa] = useState<string>('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const carregarTabela = async () => {
      try {
        setLoading(true);
        setError(null);

        const userId = await storage.getItem('userId');
        if (!userId) {
          setError('Usuário não encontrado. Faça login novamente.');
          navigate('/login');
          return;
        }

        const usuarioSnap = await getDoc(doc(db, 'usuarios', userId));
        if (!usuarioSnap.exists()) {
          setError('Dados do usuário não encontrados.');
          return;
        }

        const usuarioData = usuarioSnap.data() as any;
        const empresaId = usuarioData.empresaId;
        if (!empresaId) {
          setError('Empresa não encontrada para este usuário.');
          return;
        }

        const empresaSnap = await getDoc(doc(db, 'Empresa', empresaId));
        if (!empresaSnap.exists()) {
          setError('Dados da empresa não encontrados.');
          return;
        }

        const empresaData = empresaSnap.data() as any;
        const tabelaTaxaId = empresaData.tabelaTaxaId;
        
        // Salvar nome e foto da empresa
        setNomeEmpresa(empresaData.nomeEmpresa || empresaData.nome || 'Empresa');
        setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
        
        if (!tabelaTaxaId) {
          setError('Nenhuma tabela de taxas configurada para esta empresa.');
          return;
        }

        const tabelaSnap = await getDoc(doc(db, 'tabelasTaxas', tabelaTaxaId));
        if (!tabelaSnap.exists()) {
          setError('Tabela de taxas não encontrada.');
          return;
        }

        const tabelaData = tabelaSnap.data() as any;
        const setores: Setor[] = tabelaData.setores || [];

        setTabela({
          nome: tabelaData.nome || 'Tabela de Taxas',
          descricao: tabelaData.descricao || '',
          setores,
        });
      } catch (e) {
        console.error('Erro ao carregar tabela de taxas da empresa:', e);
        setError('Erro ao carregar tabela de taxas. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    carregarTabela();
  }, [navigate]);

  const formatBRL = (valor: number) => {
    try {
      return valor.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      const num = Number(valor || 0);
      return `R$ ${num.toFixed(2).replace('.', ',')}`;
    }
  };

  const exportarPDF = () => {
    if (!tabela || !tabela.setores || tabela.setores.length === 0) {
      return;
    }

    try {
      setExportandoPDF(true);
      
      // Agrupar setores por valor/preço
      const gruposPorPreco = new Map<number, Setor[]>();
      tabela.setores.forEach((setor) => {
        const valor = Number(setor.valor || 0);
        if (!gruposPorPreco.has(valor)) {
          gruposPorPreco.set(valor, []);
        }
        gruposPorPreco.get(valor)!.push(setor);
      });

      // Ordenar preços
      const precosOrdenados = Array.from(gruposPorPreco.keys()).sort((a, b) => a - b);

      // Criar estrutura da tabela agrupada por preço
      let headerPrecos = '';
      precosOrdenados.forEach((preco) => {
        const setoresDoPreco = gruposPorPreco.get(preco)!;
        const numColunas = setoresDoPreco.length;
        
        // Primeira célula com o preço (cinza escuro)
        headerPrecos += `<th style="background: #2c3e50; color: #fff; font-weight: bold; padding: 10px 6px; font-size: 11px; border: 1px solid #000;">${formatBRL(preco)}</th>`;
        
        // Células vazias brancas para completar as colunas do grupo
        for (let i = 1; i < numColunas; i++) {
          headerPrecos += `<th style="background: #fff; border: 1px solid #000; padding: 10px 6px;"></th>`;
        }
      });

      // Organizar setores: todos em uma única linha horizontal por grupo
      let primeiraLinha = '';
      precosOrdenados.forEach((preco) => {
        const setoresDoPreco = gruposPorPreco.get(preco)!;
        // Cabeçalho de preço em azul claro (apenas na primeira linha)
        primeiraLinha += `<td style="background: #87CEEB; color: #000; font-weight: bold; padding: 10px 6px; font-size: 10px; text-align: center; border: 1px solid #000;">${formatBRL(preco)}</td>`;
        // Setores listados horizontalmente em uma única linha
        setoresDoPreco.forEach((setor) => {
          primeiraLinha += `<td style="background: #fff; padding: 10px 6px; font-size: 9px; text-align: left; border: 1px solid #000;">${setor.nome}</td>`;
        });
      });
      const linhasTabela = `<tr>${primeiraLinha}</tr>`;

      const nomeEmpresaFinal = nomeEmpresa.toUpperCase();
      const tituloCompleto = `TABELA PADRÃO PARA ${nomeEmpresaFinal}`;

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <style>
              @page {
                size: A4;
                margin: 10mm;
              }
              * {
                box-sizing: border-box;
              }
              body { 
                font-family: Arial, sans-serif; 
                margin: 0;
                padding: 0;
                font-size: 11px;
              }
              .header-orange {
                background: #d2691e;
                color: #fff;
                text-align: center;
                padding: 14px 10px;
                font-size: 18px;
                font-weight: bold;
                border: none;
                line-height: 1.4;
              }
              .header-gray {
                background: #2c3e50;
                color: #fff;
                text-align: center;
                padding: 12px 10px;
                font-size: 16px;
                font-weight: bold;
                border: none;
                line-height: 1.4;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 0;
                font-size: 10px;
                table-layout: fixed;
              }
              th, td { 
                border: 1px solid #000; 
                padding: 10px 6px;
                font-size: 10px;
                word-wrap: break-word;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              th { 
                background: #2c3e50; 
                color: #fff; 
                text-align: center; 
                font-weight: bold;
                font-size: 11px;
              }
              td { 
                background: #fff; 
                color: #000;
                font-size: 9px;
              }
              .footer {
                margin-top: 14px;
                text-align: center;
                font-size: 9px;
                color: #000;
                padding: 10px;
                line-height: 1.5;
              }
              .logo {
                float: right;
                font-weight: bold;
                color: #2c3e50;
                font-size: 10px;
                margin-top: 8px;
              }
            </style>
          </head>
          <body>
            <div class="header-orange">${tituloCompleto}</div>
            <div class="header-gray">TAXA POR SETOR</div>
            <table>
              <thead>
                <tr>
                  ${headerPrecos}
                </tr>
              </thead>
              <tbody>
                ${linhasTabela}
              </tbody>
            </table>
            <div class="footer">
              <div>OS DEMAIS ENDEREÇOS QUE NÃO SE ENCONTRA NA TABELA COBRAR R$ 1,50 POR KM IDA E VOLTA.</div>
              <div style="margin-top: 6px;">CONSULTA DEVE SER FEITA VIA GPS OU NO VELOCIMETRO DA MOTO.</div>
              <div class="logo">ROTA ENTREGAS</div>
            </div>
          </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setExportandoPDF(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando tabela de taxas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tabela de Taxas</h1>
                <p className="text-sm text-gray-500">Visualize os valores por setor</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/empresa')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <span>←</span>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {!error && tabela && (
          <>
            {/* Card de Informações da Tabela */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{tabela.nome}</h2>
                  {tabela.descricao ? (
                    <p className="text-gray-600">{tabela.descricao}</p>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      Esta é a tabela de setores e valores utilizada para calcular o frete das suas entregas.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Botão de Exportar PDF */}
            {tabela.setores && tabela.setores.length > 0 && (
              <button
                onClick={exportarPDF}
                disabled={exportandoPDF}
                className="w-full mb-6 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {exportandoPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Exportando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Exportar PDF</span>
                  </>
                )}
              </button>
            )}

            {/* Lista de Setores */}
            {(!tabela.setores || tabela.setores.length === 0) ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📋</div>
                <p className="text-gray-600 text-lg font-medium">Nenhum setor configurado nesta tabela de taxas.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Cabeçalho da Tabela */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-white font-semibold">Setor</div>
                    <div className="text-white font-semibold text-right">Valor</div>
                  </div>
                </div>

                {/* Lista de Setores */}
                <div className="divide-y divide-gray-200">
                  {tabela.setores
                    .sort((a, b) => a.nome.localeCompare(b.nome))
                    .map((setor, index) => (
                      <div
                        key={`${setor.nome}-${index}`}
                        className="px-6 py-4 hover:bg-gray-50 transition-colors grid grid-cols-2 gap-4 items-center"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <span className="font-medium text-gray-900">{setor.nome}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-green-600">
                            {formatBRL(Number(setor.valor || 0))}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-gray-200/50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {empresaFoto ? (
                  <img
                    src={empresaFoto}
                    alt={nomeEmpresa}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{nomeEmpresa || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-amber-700">Tabela de Taxas</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default TabelaTaxas;

